import { useEffect, useState } from 'react'
import { PageHeader } from '../../components/PageHeader'
import { BackgroundTitle } from '../../components/BackgroundTitle'
import { ActiveRoomCard } from '../../components/ActiveRoomCard'
import { fetchMyActiveRooms, type ActiveRoom } from '../../lib/api/activeRooms'
import { readError } from '../../lib/errors'
import { useT } from '../../i18n'

interface Props {
  onBack: () => void
}

/**
 * The full list of running games the player is seated in. The Hub's
 * "Jump back in" section shows at most 4 tiles; when there are more,
 * its YOUR GAME ROOMS pill lands here. Same cards, same ordering
 * (your-turn rooms first, then soonest AI-takeover deadline), same
 * tap-through path as a push notification.
 *
 * The 10-game cap is enforced server-side at every join, so this list
 * can never exceed ten rows.
 */
export function MyGameRoomsScreen({ onBack }: Props) {
  const t = useT()
  const [rooms, setRooms] = useState<ActiveRoom[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    fetchMyActiveRooms()
      .then(rows => { if (!cancelled) setRooms(rows) })
      .catch(e => { if (!cancelled) setError(readError(e)) })
    return () => { cancelled = true }
  }, [tick])

  return (
    <div className="screen">
      <BackgroundTitle text={t('Your Game Rooms')} color="#74705F" />
      <PageHeader
        title={t('Your game rooms')}
        subtitle={rooms ? t('{n} of 10 running', { n: rooms.length }) : undefined}
        onBack={onBack}
      />

      <div className="flex-1 px-5 py-5 space-y-3 overflow-y-auto pb-6 relative z-10">
        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {rooms === null && !error && (
          <>
            <div className="h-16 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-16 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-16 rounded-2xl bg-brand-elevated animate-pulse" />
          </>
        )}

        {rooms?.length === 0 && (
          <p className="text-sm text-brand-muted text-center py-8">
            {t('No games running. Start one from the Games tab.')}
          </p>
        )}

        {rooms && rooms.map(r => (
          <ActiveRoomCard key={`${r.game}:${r.room_id}`} room={r}
                          onLeft={() => setTick(t => t + 1)} />
        ))}
      </div>
    </div>
  )
}
